import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime } from "@/lib/format";
import { COMM_TYPES, COMM_DIRECTIONS, VISIBILITIES, TONE, optOf, labelOf } from "@/config/commOptions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Plus, Phone, Mail, MessageCircle, MessagesSquare, Users, Video, Megaphone,
  ArrowDownLeft, ArrowUpRight, Trash2, Search, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = { Phone, Mail, MessageCircle, MessagesSquare, Users, Video, Megaphone };

const Pill = ({ arr, value, testid }) => {
  if (!value) return null;
  const o = optOf(arr, value);
  const t = TONE[o?.tone || "gray"];
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold border whitespace-nowrap"
      style={{ backgroundColor: t.bg, color: t.color, borderColor: t.border }} data-testid={testid}>
      {o?.label || labelOf(arr, value)}
    </span>
  );
};

const emptyForm = { communication_type: "call", direction: "outbound", subject: "", summary: "", body: "", visibility: "internal" };

export default function CommunicationsPanel({ relatedType = null, relatedId = null, branchId = null, showFilters = true }) {
  const { user } = useAuth();
  const canWrite = !user?.is_portal;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", communication_type: "all", direction: "all" });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (relatedType) params.related_type = relatedType;
      if (relatedId) params.related_id = relatedId;
      if (filters.q) params.q = filters.q;
      if (filters.communication_type !== "all") params.communication_type = filters.communication_type;
      if (filters.direction !== "all") params.direction = filters.direction;
      const { data } = await api.get("/communications", { params });
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, [relatedType, relatedId, filters]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/communications", { ...form, related_type: relatedType, related_id: relatedId, branch_id: branchId });
      toast.success("Communication logged");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not log communication");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/communications/${id}`); load(); } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {showFilters && (
          <>
            <div className="relative flex-1 min-w-[180px]">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={filters.q} onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))}
                placeholder="Search subject or summary..." className="pl-9 h-9" data-testid="comm-search" />
            </div>
            <Select value={filters.communication_type} onValueChange={(v) => setFilters((p) => ({ ...p, communication_type: v }))}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="comm-filter-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All types</SelectItem>{COMM_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filters.direction} onValueChange={(v) => setFilters((p) => ({ ...p, direction: v }))}>
              <SelectTrigger className="h-9 w-[140px]" data-testid="comm-filter-direction"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Any direction</SelectItem>{COMM_DIRECTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </>
        )}
        {canWrite && (
          <Button size="sm" onClick={() => { setForm(emptyForm); setOpen(true); }} className="text-white ml-auto"
            style={{ backgroundColor: "var(--mmm-red)" }} data-testid="log-communication-button">
            <Plus size={15} className="mr-1.5" /> Log communication
          </Button>
        )}
      </div>

      {loading ? (
        <div className="space-y-2"><Skeleton className="h-20 w-full" /><Skeleton className="h-20 w-full" /></div>
      ) : items.length === 0 ? (
        <div className="py-12 text-center">
          <div className="mx-auto mb-3 h-12 w-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "rgba(28,67,88,0.08)" }}>
            <MessageSquare size={22} className="text-[#1C4358]" />
          </div>
          <p className="text-sm font-medium text-[#252221]">No communications logged yet</p>
          <p className="text-sm text-muted-foreground">Log a call, email, or meeting to keep the family's history in one place.</p>
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="comm-list">
          {items.map((m) => {
            const typeOpt = optOf(COMM_TYPES, m.communication_type);
            const I = ICONS[typeOpt?.icon] || Megaphone;
            const inbound = m.direction === "inbound";
            return (
              <div key={m.id} className="rounded-xl border border-[#E6EDF2] p-3 bg-white" data-testid="comm-item">
                <div className="flex items-start gap-3">
                  <span className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(28,67,88,0.08)" }}>
                    <I size={17} className="text-[#1C4358]" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-[#252221]">{m.subject || labelOf(COMM_TYPES, m.communication_type)}</span>
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium" style={{ color: inbound ? "#256B4B" : "#1C4358" }}>
                        {inbound ? <ArrowDownLeft size={12} /> : <ArrowUpRight size={12} />}{inbound ? "Inbound" : "Outbound"}
                      </span>
                      <Pill arr={COMM_TYPES} value={m.communication_type} />
                      <Pill arr={VISIBILITIES} value={m.visibility} />
                    </div>
                    {(m.summary || m.body) && <p className="text-sm text-[#252221] mt-1 whitespace-pre-wrap leading-relaxed">{m.summary || m.body}</p>}
                    <div className="text-[11px] text-muted-foreground mt-1">{m.created_by_name || "Team"} · {formatDateTime(m.created_at)}</div>
                  </div>
                  {canWrite && (
                    <button onClick={() => remove(m.id)} className="text-muted-foreground hover:text-[#9C1E22] p-1" data-testid="delete-comm-button" title="Delete">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="comm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-lg flex items-center gap-2"><MessageSquare size={18} className="text-[#1C4358]" /> Log communication</DialogTitle>
            <DialogDescription>Record a call, email, or meeting with the family or a partner.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block text-[13px]">Type</Label>
                <Select value={form.communication_type} onValueChange={(v) => setForm((p) => ({ ...p, communication_type: v }))}>
                  <SelectTrigger data-testid="comm-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{COMM_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block text-[13px]">Direction</Label>
                <Select value={form.direction} onValueChange={(v) => setForm((p) => ({ ...p, direction: v }))}>
                  <SelectTrigger data-testid="comm-direction-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{COMM_DIRECTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block text-[13px]">Subject</Label>
              <Input value={form.subject} onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))} placeholder="e.g. Follow-up on placement options" data-testid="comm-subject" />
            </div>
            <div>
              <Label className="mb-1.5 block text-[13px]">Summary / notes</Label>
              <Textarea rows={3} value={form.summary} onChange={(e) => setForm((p) => ({ ...p, summary: e.target.value }))} placeholder="What was discussed?" data-testid="comm-summary" />
            </div>
            <div>
              <Label className="mb-1.5 block text-[13px]">Visibility</Label>
              <Select value={form.visibility} onValueChange={(v) => setForm((p) => ({ ...p, visibility: v }))}>
                <SelectTrigger data-testid="comm-visibility-select"><SelectValue /></SelectTrigger>
                <SelectContent>{VISIBILITIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="save-comm-button">{busy ? "Saving..." : "Log it"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
